/**
 * **Disposable fixture — Integrations overview.**
 *
 * Authored because the overview has no endpoint and no DTO to derive from: it is
 * a cross-kind health rollup over two halves of the estate, one of which
 * (Collectors) has no backend at all yet. Every figure here is a number the
 * design printed, not one the platform can currently produce.
 *
 * **One file on purpose.** The needs-attention rows sit next to the counts that
 * summarise them, because authoring them apart is how a strip ends up saying
 * `3` above four rows. The KPI strip derives its Needs-attention count *and* its
 * `1 disconnected · 1 offline · 1 degraded` caption from
 * {@link NEEDS_ATTENTION_FIXTURES}, so that particular drift cannot happen at
 * all; the remaining figures are adjacent so the rest is at least visible.
 *
 * **The Collector figures duplicate `../../collectors/fixtures/`** — the same
 * four agents, three online, four sites, twelve Log Sources. They are repeated
 * rather than imported because the real overview will be one endpoint's
 * response, not a client-side join over the Collectors list, and importing here
 * would build a seam the backend is not going to have.
 * `__tests__/fixture-agreement.test.ts` fails if the two ever disagree.
 *
 * **Delete condition:** the first real integrations-overview endpoint. When it
 * lands this file is deleted and `../api.ts` calls the endpoint.
 *
 * **Not demo-world content** (ADR 0017): the demo build authors its own data
 * from the real contract and must never import this.
 *
 * Times are `…SecondsAgo` offsets rather than timestamps, resolved against the
 * clock in `../api.ts` — an absolute date authored today reads as "3 days ago"
 * next week.
 */
import type {
	CloudEstateSummary,
	CollectorEstateSummary,
	LogVolumeSummary,
	NeedsAttentionEntry,
} from '../types'

export type CloudEstateFixture = Omit<CloudEstateSummary, 'lastSyncAt'> & {
	lastSyncSecondsAgo: number
}

export type CollectorEstateFixture = Omit<
	CollectorEstateSummary,
	'lastCheckInAt'
> & {
	lastCheckInSecondsAgo: number
}

/**
 * Everything unhealthy, both kinds, in the design's order.
 *
 * `status` is the raw value of the row's **own** vocabulary: `error` is Cloud
 * health (which reads *Disconnected*), `offline` and `degraded` are Collector
 * liveness. The presets do the spelling — see `../kind-preset.ts`.
 *
 * The two Collector rows carry the ids of real Collector fixtures, so their
 * Manage links land on the detail page for the agent the row is complaining
 * about.
 *
 * **Empty this array to see the all-clear variant.** It is the only way in: the
 * page has no server to change its mind, and a URL switch for it would be
 * product surface nobody asked for.
 */
export const NEEDS_ATTENTION_FIXTURES: NeedsAttentionEntry[] = [
	{
		id: 'cloud-crowdstrike-falcon',
		kind: 'cloud',
		name: 'CrowdStrike Falcon',
		reason: 'Auth token expired 2h ago — no events received since',
		status: 'error',
	},
	{
		id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a804',
		kind: 'collector',
		name: 'St. Johns, MI',
		reason: 'No check-in for 14m · 3 log sources affected',
		status: 'offline',
	},
	{
		id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a803',
		kind: 'collector',
		name: 'Branch — Austin, TX',
		// Not the design's "EPS down ~60% vs 7-day baseline · 318 eps": EPS is on
		// the backend's list of numbers nothing can supply, and the Collectors list
		// already declined to invent it. The heartbeat gap is the same fact stated
		// in a unit the agent actually reports.
		reason: 'Check-ins arriving late — last one 2m ago · 1 source silent',
		status: 'degraded',
	},
]

export const CLOUD_ESTATE_FIXTURE: CloudEstateFixture = {
	connected: 11,
	disconnected: 1,
	withResponseCapability: 5,
	lastSyncSecondsAgo: 120,
}

export const COLLECTOR_ESTATE_FIXTURE: CollectorEstateFixture = {
	total: 4,
	online: 3,
	siteCount: 4,
	sourceCount: 12,
	lastCheckInSecondsAgo: 3,
}

/**
 * The design's `680 GB ingested last 24h`, carried as the two strings it is
 * printed as. There is no source for this number — it is cross-kind and no
 * existing endpoint measures it — so there is nothing to compute it from.
 */
export const LOG_VOLUME_FIXTURE: LogVolumeSummary = {
	value: '680 GB',
	caption: 'ingested last 24h',
}

/** How long ago the health rollup ran — the all-clear band's timestamp. */
export const LAST_CHECKED_SECONDS_AGO = 120
